import { Box, GlobalStyles, type SxProps, type Theme } from "@mui/material";
import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_CSS_VARS,
  SITE_FONT_FAMILY,
  SITE_LINE_HEIGHT,
} from "src/domains/shared/constants/styles/site";
import {
  registerSiteHeaderTopProperty,
  SITE_GLOBAL_STYLES,
} from "./siteGlobalStyles";
import { SITE_FONT_STYLESHEET_HREF } from "./siteRoutes";

const SITE_ROOT_STYLES = {
  ...SITE_CSS_VARS,
  background: SITE_COLOR.bg,
  color: SITE_COLOR.ink,
  fontFamily: SITE_FONT_FAMILY,
  fontSize: "17px",
  lineHeight: SITE_LINE_HEIGHT,
  letterSpacing: "-.01em",
  // 한국어는 낱말 중간에서 줄을 바꾸지 않는다(시안). 띄어쓰기 없이 이어 쓰는 일본어에 걸면 줄을 못 바꿔 넘치므로 한국어에만
  "&:lang(ko)": { wordBreak: "keep-all" },
  overflowWrap: "break-word",
  WebkitFontSmoothing: "antialiased",
  textRendering: "optimizeLegibility",
  WebkitTapHighlightColor: "transparent",
} as const;

const DRAG_BLOCKED_SELECTOR = "img, svg, a, picture, video, canvas";

/** 앱 화면에서 넘어온 경우(문서 머리가 사이트용이 아님) 사이트 글꼴을 이어 붙인다. */
const ensureSiteFontStylesheet = () => {
  if (document.querySelector(`link[href="${SITE_FONT_STYLESHEET_HREF}"]`))
    return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = SITE_FONT_STYLESHEET_HREF;
  document.head.appendChild(link);
};

/**
 * 사이트 화면의 뿌리(marketing-site-shell·marketing-auth-pages): 사이트 토큰·글꼴·글자 기본값과 사이트 전용 전역 규칙을 이 안에만
 * 건다. 화면 언어를 `lang` 으로 단다(보조 기술 발음·언어별 줄바꿈 규칙). 사이트 틀과 로그인 계열 화면이 함께 쓴다.
 */
export function SiteRoot({
  children,
  sx,
}: {
  children: ReactNode;
  sx?: SxProps<Theme>;
}) {
  const { i18n } = useTranslation();

  // 문서 언어도 화면 언어를 따른다 — 보조 기술 발음·검색 엔진 언어 판단(서버 렌더는 _document 의 ko)
  useEffect(() => {
    if (i18n.language) document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    registerSiteHeaderTopProperty();
    ensureSiteFontStylesheet();
    // 그림·링크를 끌어도 따라 나오지 않게(CSS -webkit-user-drag 가 안 먹는 브라우저 대비)
    const blockDrag = (event: DragEvent) => {
      const target = event.target as Element | null;
      if (target?.closest?.(DRAG_BLOCKED_SELECTOR)) event.preventDefault();
    };
    document.addEventListener("dragstart", blockDrag);
    return () => document.removeEventListener("dragstart", blockDrag);
  }, []);

  return (
    <Box
      data-site=""
      lang={i18n.language}
      sx={[SITE_ROOT_STYLES, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <GlobalStyles styles={SITE_GLOBAL_STYLES} />
      {children}
    </Box>
  );
}
